/**
 * Panel "Comentarios" de un proyecto en el admin: lista de conversaciones
 * (general + una por tarea) con no leídos, consultas pendientes y último
 * mensaje; a la derecha, el hilo seleccionado para responder o dejar notas
 * internas.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { MessageSquare, AlertCircle, Loader } from "lucide-react";
import ComentariosHilo from "./ComentariosHilo";
import {
  resumenComentarios, suscribirComentarios, mensajeError, hiloDe, HILO_GENERAL,
} from "../../lib/comentarios";
import { fmtRelativo } from "../../lib/formato";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  err:"#ef4444", errL:"#fee2e2", warn:"#f59e0b", warnL:"#fef3c7",
};

export default function ComentariosProyectoAdmin({ proyecto, tareas = [], hiloInicial = HILO_GENERAL, onActividad }) {
  const [resumen,  setResumen]  = useState({});
  const [cargando, setCargando] = useState(true);
  const [error,    setError]    = useState("");
  const [hilo,     setHilo]     = useState(hiloInicial || HILO_GENERAL);

  const cargarResumen = useCallback(async () => {
    try {
      const filas = await resumenComentarios(proyecto.id);
      const porHilo = {};
      filas.forEach(r => { porHilo[hiloDe(r.tarea_id)] = r; });
      setResumen(porHilo);
      setError("");
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setCargando(false);
    }
  }, [proyecto.id]);

  useEffect(() => { cargarResumen(); }, [cargarResumen]);
  useEffect(() => suscribirComentarios(proyecto.id, { onInsert: () => cargarResumen() }),
    [proyecto.id, cargarResumen]);

  const actividad = useCallback(() => { cargarResumen(); onActividad?.(); }, [cargarResumen, onActividad]);

  const hilos = useMemo(() => {
    const lista = [{ key: HILO_GENERAL, tareaId: null, nombre: "Comentarios y consultas generales" }]
      .concat(tareas.map(t => ({ key: t.id, tareaId: t.id, nombre: t.descripcion, estado: t.estado })));
    const peso = h => {
      const r = resumen[h.key];
      if (h.key === HILO_GENERAL) return 3;
      if (r?.pendiente_respuesta) return 2;
      if (r?.total) return 1;
      return 0;
    };
    return lista.sort((a, b) => peso(b) - peso(a)
      || new Date(resumen[b.key]?.ultimo_at || 0) - new Date(resumen[a.key]?.ultimo_at || 0));
  }, [tareas, resumen]);

  const actual = hilos.find(h => h.key === hilo) || hilos[0];

  return (
    <div style={{flex:1, overflow:"auto", padding:"20px 24px"}}>
      <div className="coment-admin-grid">
        {/* Lista de conversaciones */}
        <nav aria-label="Conversaciones del proyecto"
          style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
            overflow:"hidden", boxShadow:"0 1px 3px #0000000a", alignSelf:"start"}}>
          <div style={{padding:"12px 14px", borderBottom:`1px solid ${C.border}`,
            fontSize:11, fontWeight:700, color:C.sub, letterSpacing:.5, textTransform:"uppercase"}}>
            Conversaciones
          </div>
          {cargando && (
            <div style={{padding:16, fontSize:12, color:C.sub, display:"flex", gap:6, alignItems:"center"}}>
              <Loader size={12} className="spinning" aria-hidden="true"/> Cargando...
            </div>
          )}
          {error && (
            <div role="alert" style={{margin:10, padding:"8px 10px", borderRadius:7, fontSize:12,
              background:C.errL, color:C.err, display:"flex", gap:6, alignItems:"center"}}>
              <AlertCircle size={13} aria-hidden="true"/> {error}
            </div>
          )}
          <ul style={{listStyle:"none", margin:0, padding:0, maxHeight:560, overflowY:"auto"}}>
            {hilos.map(h => {
              const r = resumen[h.key];
              const activo = actual?.key === h.key;
              return (
                <li key={h.key}>
                  <button type="button" onClick={() => setHilo(h.key)}
                    aria-current={activo ? "true" : undefined}
                    style={{width:"100%", textAlign:"left", border:"none", cursor:"pointer",
                      padding:"10px 14px", borderBottom:`1px solid ${C.border}`,
                      background: activo ? C.accentL : C.white,
                      borderLeft:`3px solid ${activo ? C.accent : "transparent"}`,
                      fontFamily:"'DM Sans',sans-serif"}}>
                    <div style={{display:"flex", alignItems:"center", gap:6}}>
                      {h.key === HILO_GENERAL && <MessageSquare size={12} color={C.accent} aria-hidden="true"/>}
                      <span style={{flex:1, fontSize:13, fontWeight: r?.nuevos ? 700 : 600, color:C.text,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                        {h.nombre}
                      </span>
                      {r?.pendiente_respuesta && (
                        <span title="Consulta del cliente pendiente de respuesta"
                          style={{fontSize:10, fontWeight:700, color:C.warn, background:C.warnL,
                            padding:"1px 6px", borderRadius:8}}>Pendiente</span>
                      )}
                      {r?.nuevos > 0 && (
                        <span aria-label={`${r.nuevos} sin leer`}
                          style={{minWidth:18, height:18, borderRadius:9, background:C.accent,
                            color:"#fff", fontSize:10, fontWeight:700, display:"inline-flex",
                            alignItems:"center", justifyContent:"center", padding:"0 5px"}}>
                          {r.nuevos}
                        </span>
                      )}
                    </div>
                    <div style={{fontSize:11, color:C.light, marginTop:3,
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      {r
                        ? <>{r.ultimo_es_interno ? "🔒 " : ""}<strong style={{color:C.sub, fontWeight:600}}>
                            {r.ultimo_autor}:</strong> {r.ultimo_texto} · {fmtRelativo(r.ultimo_at)}</>
                        : (h.estado ? `${h.estado} · sin comentarios` : "Sin comentarios")}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Hilo seleccionado */}
        <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
          padding:"16px 18px", boxShadow:"0 1px 3px #0000000a", minWidth:0}}>
          {actual && (
            <ComentariosHilo key={actual.key}
              proyectoId={proyecto.id} tareaId={actual.tareaId} modo="admin"
              titulo={actual.tareaId ? `Tarea: ${actual.nombre}` : "Comentarios y consultas"}
              alturaMax={460} onActividad={actividad}/>
          )}
        </div>
      </div>
    </div>
  );
}
